// One function per attendee endpoint in docs/API.md.
import { del, get, post, put, patch } from './client'
import type {
  AgendaResponse, AuthMe, BonusClaim, EventPublic, Install, InstallInput, LeaderRow, MeetState, Match, Me,
  MeUpdate, Note, Person, PlanInput, PlanResponse, PointsResponse, Resource, SurveyInput, VerifyBody, VerifyResponse,
} from './types'

const e = (slug: string) => `/events/${encodeURIComponent(slug)}`

// auth
export const authRequest = (body: { email: string; event_slug?: string; next?: string }) =>
  post<{ sent: true; cooldown_s: number }>('/auth/request', body)
export const authVerify = (body: VerifyBody) => post<VerifyResponse>('/auth/verify', body)
export const authMe = () => get<AuthMe>('/auth/me')
export const authSignOut = () => post<void>('/auth/signout')

// public
export const getEvent = (slug: string) => get<EventPublic>(e(slug))
/** Which event this host name serves (`settings.hosts`); 404 `no_event_for_host` when none. */
export const getResolve = () => get<{ slug: string }>('/events/resolve')

// attendee
export const getMe = (slug: string) => get<Me>(`${e(slug)}/me`)
export const putMe = (slug: string, body: MeUpdate) => put<Me>(`${e(slug)}/me`, body)
export const postOpened = (slug: string) => post<void>(`${e(slug)}/me/opened`)

export type PeopleQuery = { segment?: string; filter?: 'all' | 'first' | 'met' | 'flag'; q?: string }
export function getPeople(slug: string, query: PeopleQuery = {}) {
  const qs = new URLSearchParams()
  if (query.segment) qs.set('segment', query.segment)
  if (query.filter) qs.set('filter', query.filter)
  if (query.q) qs.set('q', query.q)
  const s = qs.toString()
  return get<{ people: Person[]; total: number }>(`${e(slug)}/people${s ? `?${s}` : ''}`)
}
export const getMatches = (slug: string) => get<{ matches: Match[]; published_at: string | null }>(`${e(slug)}/matches`)
export const postMeet = (slug: string, personId: string) => post<MeetState>(`${e(slug)}/meets/${encodeURIComponent(personId)}`)
export const deleteMeet = (slug: string, personId: string) => del<MeetState>(`${e(slug)}/meets/${encodeURIComponent(personId)}`)
export const getNote = (slug: string, personId: string) => get<Note>(`${e(slug)}/notes/${encodeURIComponent(personId)}`)
export const putNote = (slug: string, personId: string, body: Note) => put<Note>(`${e(slug)}/notes/${encodeURIComponent(personId)}`, body)

export const getAgenda = (slug: string) => get<AgendaResponse>(`${e(slug)}/agenda`)
export const postCheckin = (slug: string, sessionId: string) =>
  post<{ checked_in: true; moved_from?: string }>(`${e(slug)}/sessions/${encodeURIComponent(sessionId)}/checkin`)
export const deleteCheckin = (slug: string, sessionId: string) => del<void>(`${e(slug)}/sessions/${encodeURIComponent(sessionId)}/checkin`)
export const getHere = (slug: string, sessionId: string) =>
  get<{ people: Person[]; count: number }>(`${e(slug)}/sessions/${encodeURIComponent(sessionId)}/here`)
export const getResources = (slug: string, sessionId: string) =>
  get<{ resources: Resource[]; attachments: Resource[] }>(`${e(slug)}/sessions/${encodeURIComponent(sessionId)}/resources`)
export const postSurvey = (slug: string, sessionId: string, body: SurveyInput) =>
  post<{ submitted_at: string; points_awarded: number }>(`${e(slug)}/sessions/${encodeURIComponent(sessionId)}/survey`, body)
export const postEventSurvey = (slug: string, answers: Record<string, unknown>) => post<{ submitted_at: string }>(`${e(slug)}/survey`, { answers })

export const getInstalls = (slug: string) => get<Install[]>(`${e(slug)}/installs`)
export const postInstall = (slug: string, body: InstallInput) => post<Install>(`${e(slug)}/installs`, body)
export const patchInstall = (slug: string, id: string, body: Partial<InstallInput>) => patch<Install>(`${e(slug)}/installs/${encodeURIComponent(id)}`, body)
export const deleteInstall = (slug: string, id: string) => del<void>(`${e(slug)}/installs/${encodeURIComponent(id)}`)

export const getPlan = (slug: string) => get<PlanResponse>(`${e(slug)}/plan`)
export const putPlan = (slug: string, body: PlanInput) => put<PlanResponse>(`${e(slug)}/plan`, body)
export const postPlanEmail = (slug: string) => post<{ sent: true }>(`${e(slug)}/plan/email`)

export const getPoints = (slug: string) => get<PointsResponse>(`${e(slug)}/points`)
export const putAllocations = (slug: string, allocations: Record<string, number>) =>
  put<PointsResponse>(`${e(slug)}/points/allocations`, { allocations })
export const postTableCode = (slug: string, code: string) => post<{ points: number }>(`${e(slug)}/table-codes/redeem`, { code })
export const getBonus = (slug: string) => get<BonusClaim[]>(`${e(slug)}/bonus`)
export const postBonus = (slug: string, key: string) => post<BonusClaim>(`${e(slug)}/bonus/${encodeURIComponent(key)}`)
export const getRock = (slug: string) => get<{ completed_at: string | null }>(`${e(slug)}/rock`)
export const postRock = (slug: string, body: { answers: Record<string, unknown>; follow_up_opt_in: boolean }) =>
  post<{ completed_at: string }>(`${e(slug)}/rock`, body)
export const postHelp = (slug: string, text: string) => post<void>(`${e(slug)}/help`, { text })
export const postAddRequest = (slug: string, body: { email: string; name: string; note: string }) => post<void>(`${e(slug)}/requests`, body)
export const getLeaderboard = (slug: string) => get<{ rows: LeaderRow[] }>(`${e(slug)}/leaderboard`)
